import type { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import frenchi18n from '@assets/i18n/fr.json';
import { TranslateModule } from '@ngx-translate/core';
import { isProbablyTouchDevice } from '@shared/models/crossDeviceCapability';
import { TrackerService } from '@shared/services/tracker.service';
import { browserBroadcastToken } from 'src/conf/di/angularProviderMapper';

/*
  The tab closes on its own after a short delay: on a computer the user is
  brought back to the widget without doing anything, and a button lets them
  leave sooner. On a phone or tablet the page almost never sits in a tab a
  script opened (the bank app opened it, or a qr scan did): no script can
  close it, so no button pretends to — the copy tells the user to close the
  page themselves.
*/
export const OUTCOME_AUTO_CLOSE_DELAY_IN_MS = 5 * 1000;

@Component({
  selector: 'app-connection-to-external-bank-ok',
  templateUrl: './connection-to-external-bank-ok.component.html',
  standalone: true,
  imports: [TranslateModule],
})
export class ConnectionToExternalBankOkComponent implements OnInit, OnDestroy {
  @Input() displayCrossDeviceCopy = false;

  readonly displayCloseButton = !isProbablyTouchDevice();

  private autoCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private tracker: TrackerService,
    @Inject(browserBroadcastToken) private broadcastService: BrowserBroadcast
  ) {}

  ngOnInit(): void {
    this.tracker.trackPage({ taggingPage: 'CONNECTION_EXTERNAL_BANK_OK' });
    this.autoCloseTimer = setTimeout(() => this.close(), OUTCOME_AUTO_CLOSE_DELAY_IN_MS);
  }

  ngOnDestroy(): void {
    if (this.autoCloseTimer !== null) {
      clearTimeout(this.autoCloseTimer);
    }
  }

  /* The user asked for it: worth a click event, unlike the automatic close. */
  closeTab(): void {
    this.tracker.trackClick({
      taggingPage: 'CONNECTION_EXTERNAL_BANK_OK',
      click: frenchi18n.close_tab,
    });

    this.close();
  }

  private close(): void {
    this.broadcastService.publish({ label: 'CLOSE_WEBAUTH_POPUP', data: null });
    window.close();
  }
}
